import { Button, Card, CardContent, CircularProgress, Typography } from '@mui/material';
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { LoadingButton } from '@mui/lab';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import Countdown from 'react-countdown';
import TestInfoCard from '../../../../components/TestInfoCard';
import PencilIcon from '../../../../svg/PencilIcon';
import palette from '../../../../theme/palette';
import MarkingSchema from '../../../../components/MarkingSchema';
import GeneralInstruction from '../../../../components/GeneralInstruction';
import NameAndChangeToScreenCard from '../../../../components/NameAndChangeToScreenCard';
// import EyeIcon from '../../../../svg/EyeIcon';
import FileUploadIcon from '../../../../svg/FileUploadIcon';
import DeleteIcon from '../../../../svg/DeleteIcon';
import '../../../../css/upcomingTest.css';
import PreviewPaper from './Teacher.PreviewPaper';
import UploadPaper from './Teacher.UploadPaper';
import Header from '../../../../components/Header';
import { ConvertToBase64 } from '../../../../helper/commonHelper';
import AddNewTestModal from '../../../../components/AddNewTestModal';
import { deleteTestById, getTestById, updateTestById } from '../../../../service/test.service';
import { uploadFile } from '../../../../service/upload.file.service';

export default function UpcomingTestDetails({ testDetails, onBack, handleDeleteTest }) {
  const [testObj, setTestObj] = useState(testDetails);
  const [selectedScreen, setSelScreen] = useState(null);
  const [editModalOpen, setEditModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isBtnLoading, setBtnLoading] = useState(false);
  const classData = useSelector((state) => state.classes.selectedClass);
  const {
    name,
    type,
    createdAt,
    // startDate,
    startTime,
    duration,
    incorrectMark,
    correctMark,
    totalMark
  } = testObj;
  const currentUser = useSelector((state) => state.auth.currentUser);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  // test started
  const [testStarted, setTestStarted] = useState(false);

  const refetchData = async () => {
    setIsLoading(true);
    const res = await getTestById(testObj.id);
    if (res) {
      setTestObj(res);
      setIsLoading(false);
    }
  };

  const updateTestWithSolution = async (file) => {
    const key = enqueueSnackbar('File Uploading', {
      variant: 'loading',
      persist: true,
      className: 'loading-variant'
    });
    const base64 = await ConvertToBase64(file);
    const fileObj = {
      base64,
      fileType: file.type,
      fileName: file.name,
      uploadFor: 'assignment',
      userId: currentUser.id,
      orgId: classData.orgID
    };
    const fileRes = await uploadFile(classData.id, fileObj);
    if (fileRes) {
      const assignmentObj = {
        isSolutionUploaded: true,
        solutionURL: fileRes.fileURL
      };
      const testRes = await updateTestById(testObj.id, assignmentObj);
      if (testRes) {
        closeSnackbar(key);
        enqueueSnackbar('Solution Uploaded', {
          variant: 'success'
        });
      } else {
        closeSnackbar(key);
        enqueueSnackbar('Please try again later', {
          variant: 'error'
        });
      }
    } else {
      closeSnackbar(key);
      enqueueSnackbar('Please try again later', {
        variant: 'error'
      });
    }
  };

  const deleteTest = async () => {
    setBtnLoading(true);
    const res = await deleteTestById(testObj.id);
    if (res) {
      handleDeleteTest(testObj.id);
      onBack();
    }
    setBtnLoading(false);
  };

  useEffect(() => {
    setTestStarted(moment().isAfter(startTime));
  }, [testObj]);

  return (
    <div style={{ marginTop: 20 }}>
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '20% 0' }}>
          <CircularProgress />
        </div>
      ) : (
        <>
          {/* eslint-disable-next-line */}
          {selectedScreen === null ? (
            <>
              <div className="nameAndActionContainer">
                <Header title={name} onBack={onBack} noMargin />
                <div
                  style={{
                    borderRadius: 50,
                    backgroundColor: testStarted ? '#17E21F' : palette.secondary.main,
                    color: 'white',
                    padding: 10,
                    fontSize: 12
                  }}
                >
                  {testStarted ? (
                    'Test Started'
                  ) : (
                    <Countdown
                      date={moment(startTime).format()}
                      renderer={(props) => (
                        <Typography
                          m={0}
                          fontSize={12}
                          // p="10px"
                          color="white"
                          // eslint-disable-next-line react/prop-types
                        >{`Starts in ${props?.hours}:${props?.minutes}:${props?.seconds}`}</Typography>
                      )}
                      onComplete={() => setTestStarted(true)}
                    />
                  )}
                </div>
              </div>
              <div className="nameAndActionContainer" style={{ justifyContent: 'flex-end' }}>
                {!testStarted && (
                  <div className="testActionButtons">
                    <Button onClick={() => setEditModal(true)} disabled={isBtnLoading}>
                      <PencilIcon color={palette.primary.main} style={{ marginRight: 10 }} /> Edit
                      Test
                    </Button>
                    <LoadingButton
                      loading={isBtnLoading}
                      style={{ color: 'red', marginLeft: 10 }}
                      onClick={() => deleteTest()}
                    >
                      <DeleteIcon color="red" style={{ marginRight: 10 }} /> Delete Test
                    </LoadingButton>
                  </div>
                )}
              </div>
              <TestInfoCard
                createdOn={moment(createdAt).format('DD MMM, LT')}
                startTime={moment(startTime).format('DD MMM, LT')}
                endTime={moment(startTime).add(duration, 'minutes').format('DD MMM, LT')}
                duration={`${duration} minutes`}
                questions={type === 'type' ? testObj.questions.length : null}
                marks={totalMark}
              />
              <MarkingSchema
                correctAns={`+${correctMark}`}
                incorrectAns={incorrectMark < 0 ? `-${incorrectMark}` : incorrectMark}
              />
              {testObj?.description && (
                <GeneralInstruction instructionList={testObj?.description.split(',')} />
              )}
              {type !== 'type' && (
                <Card style={{ marginTop: 25 }}>
                  <CardContent>
                    {/* <NameAndChangeToScreenCard
                      title="Preview Test Paper"
                      icon={<EyeIcon color={palette.secondary.main} />}
                      onClick={() => setSelScreen('preview')}
                    /> */}
                    <NameAndChangeToScreenCard
                      title="Upload Solution (optional)"
                      icon={<FileUploadIcon color={palette.secondary.main} />}
                      onClick={() => setSelScreen('upload')}
                    />
                  </CardContent>
                </Card>
              )}
            </>
          ) : selectedScreen === 'preview' ? (
            <PreviewPaper onBack={() => setSelScreen(null)} />
          ) : (
            <UploadPaper
              fileObj={testObj?.solutionURL ? { name: testObj?.solutionURL } : null}
              onBack={() => {
                refetchData();
                setSelScreen(null);
              }}
              onFileSelect={(f) => updateTestWithSolution(f)}
            />
          )}
        </>
      )}
      {editModalOpen && (
        <AddNewTestModal
          isOpen={editModalOpen}
          editObj={testObj}
          isClose={() => {
            setEditModal(false);
            refetchData();
          }}
        />
      )}
    </div>
  );
}

UpcomingTestDetails.propTypes = {
  testDetails: PropTypes.object,
  onBack: PropTypes.func,
  handleDeleteTest: PropTypes.func
};
